import {Component, OnDestroy, OnInit} from "@angular/core";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {wholeNumberValidator} from "../../../components/validators/whole-number-validator";
import {notBlankValidator} from "../../../components/validators/not-blank-validator";
import {REPORT_AMOUNT, REPORT_LIMITS} from "../../../constants/dataverse-field-limits";
import {ActivatedRoute, Router} from "@angular/router";
import {ProjectService} from 'src/app/services/project.service';
import {ProjectResponse} from 'src/app/models/projects/projectResponse';
import {ReportInfoResponse} from 'src/app/models/grantApplication/grantApplicationReportModel';
import {GrantApplicationReportService} from 'src/app/services/grantApplicationReport.service';
import {NgxSpinnerService} from "ngx-spinner";
import swal from "sweetalert2";
import Swal from "sweetalert2";
import {FinancialDocumentType} from "../../../components/dropdown-upload-form/dropdown-upload-form.component";
import {Observable, Subscription} from "rxjs";
import {ErrorEventService, SharedError} from "../../../services/error.event.service";

@Component({
  selector: 'app-application-report',
  templateUrl: './application-report.component.html',
  styleUrls: ['./application-report.component.scss']
})
export class ApplicationReportComponent implements OnInit,OnDestroy
{
  id: string = "";
  application: string = "";
  projectData: ProjectResponse | null = null;
  reportData: ReportInfoResponse | null = null;
  previousUrl: string | null = null;

  documentType: FinancialDocumentType[] = []

  fileName: string = 'Choose file';
  /** ctg_report column constraints - see src/app/constants/dataverse-field-limits.ts */
  protected readonly reportLimits = REPORT_LIMITS;
  protected readonly reportAmount = REPORT_AMOUNT;

  /**
   * Format rules for the amount fields, kept separate from `Validators.required` so
   * `updateValidatorsBasedOnReportType()` can drop "required" for non-narrative report types
   * without also discarding the range checks that Dataverse enforces.
   */
  private readonly amountFormatValidators = [
    Validators.min(REPORT_AMOUNT.min),
    Validators.max(REPORT_AMOUNT.max),
    wholeNumberValidator()
  ];

  applicationForm = this.fb.group({
    amountReceive: new FormControl('',[Validators.required, ...this.amountFormatValidators]),
    amountSpent: new FormControl('', [Validators.required, ...this.amountFormatValidators]),
    budgetNextPeriod: new FormControl('',[Validators.required, ...this.amountFormatValidators]),
    // Display only: ctg_amountremaining is a Dataverse formula column (read-only, decimal with
    // precision 0, and it can legitimately be negative). It is never submitted, so it carries no
    // validators - requiring it would block the form on a value the user cannot supply.
    amountRemaining: new FormControl(''),
    isCofunded: new FormControl(''),
    // The five narrative answers are single-line String columns on ctg_report, and this page posts
    // JSON, so a plain maxLength matches exactly what the server receives. Left optional.
    cofundingDescription: new FormControl('', [notBlankValidator(), Validators.maxLength(REPORT_LIMITS.cofundingDescription)]),
    projectImplementDescription: new FormControl('', [notBlankValidator(), Validators.maxLength(REPORT_LIMITS.projectImplementDescription)]),
    otherPartnerrAndNetwork: new FormControl('', [notBlankValidator(), Validators.maxLength(REPORT_LIMITS.otherPartnerrAndNetwork)]),
    crossCuttingIssue: new FormControl('', [notBlankValidator(), Validators.maxLength(REPORT_LIMITS.crossCuttingIssue)]),
    whatStory: new FormControl('', [notBlankValidator(), Validators.maxLength(REPORT_LIMITS.whatStory)]),
    financialTasks: this.fb.array([]),
    documentDatas: this.fb.array([]),
    targetGroupImpact: this.fb.array([])
  });

  private errorEventSubscription: Subscription | null = null;
  private readonly amountRemainingSubscriptions = new Subscription();


  constructor(
    private spinner: NgxSpinnerService,
    private router: Router,
    private projectService: ProjectService,
    private grantApplicationReportService: GrantApplicationReportService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private errorEventService: ErrorEventService,
  ) {
    // Store the previous URL if available
    this.previousUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString() || null;
  }

  ngOnDestroy(): void {
    if (this.errorEventSubscription) {
      this.errorEventSubscription.unsubscribe();
    }
    this.amountRemainingSubscriptions.unsubscribe();
  }

  /**
   * Keeps the read-only Amount Remaining box in step with the two amounts above it as the user
   * types, instead of leaving a stale figure until CRM recalculates on save.
   *
   * This mirrors the Dataverse formula on ctg_amountremaining exactly:
   *
   *   ctg_transferred_amount - ctg_spent_amount
   *
   * Verified against all 68 existing reports. Note CRM treats a blank source field as 0 rather
   * than producing a blank result, which is why `Number('')` (also 0) is the right coercion here -
   * 23 reports have both amounts empty and a stored remaining of 0.
   *
   * The value is never submitted: the column is a formula column that Dataverse refuses writes to,
   * so this is a display aid and CRM stays the source of truth.
   */
  private setupAmountRemainingCalculation(): void {
    const amountReceive = this.applicationForm.get('amountReceive');
    const amountSpent = this.applicationForm.get('amountSpent');
    const amountRemaining = this.applicationForm.get('amountRemaining');

    if (!amountReceive || !amountSpent || !amountRemaining) return;

    const toNumber = (value: unknown): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const recalculate = () => {
      const remaining = toNumber(amountReceive.value) - toNumber(amountSpent.value);
      amountRemaining.setValue(String(remaining), {emitEvent: false});
    };

    this.amountRemainingSubscriptions.add(amountReceive.valueChanges.subscribe(recalculate));
    this.amountRemainingSubscriptions.add(amountSpent.valueChanges.subscribe(recalculate));
  }


  get financialTasksFormArray(): FormArray {
    return this.applicationForm.get('financialTasks') as FormArray;
  }

  get targetGroupImpact(): FormArray {
    return this.applicationForm.get("targetGroupImpact") as FormArray;
  }



  ngOnInit(): void {
    this.route.params.subscribe(async params => {
      this.id = params['id'];
      await this.loadReport();
      this.updateValidatorsBasedOnReportType();
    });

    this.errorEventSubscription = this.errorEventService.errorEvent$.subscribe(async (error: SharedError) => {
      console.log('Received error event in component:', error);
      await this.handleError(error);
    });

    // Wired after the route subscription above so that loadReport()'s patchValue still paints the
    // value CRM calculated; from then on the user's edits drive it.
    this.setupAmountRemainingCalculation();

    // If previousUrl wasn't captured in the constructor, try to get it from the Location service
    if (!this.previousUrl) {
      const state = window.history.state;

      if (state && state.navigationId > 1) {
        this.previousUrl = '/applications';
      }
    }
  }

  async onSubmit(): Promise<void> {
    await this.handleReportAction(
      (data) => this.grantApplicationReportService.submitReport(data),
      'The report has been submitted successfully.'
    );
  }

  async onSaveDraft(): Promise<void> {
    await this.handleReportAction(
      (data) => this.grantApplicationReportService.saveDraftReport(data),
      'The report draft has been saved successfully.'
    );
  }

  documentDatas(): FormArray {
    return this.applicationForm.get("documentDatas") as FormArray;
  }

  async navigateToDetails(): Promise<void> {
    if (this.previousUrl) {
      // Navigate back to the previous URL if available
      await this.router.navigateByUrl(this.previousUrl);
    } else {
      // Fall back to the application details page if no previous URL is available
      await this.router.navigate(['/applications', 'details', this.reportData?.grantApplicationId]);
    }
  }

  private async handleReportAction(
    serviceMethod: (data: any) => Observable<any>,
    successMessage: string
  ): Promise<void> {
    this.applicationForm.markAllAsTouched();

    if (this.applicationForm.invalid) {
      const invalidFields: string[] = [];
      let hasFileSizeError = false;
      let hasFileTypeError = false;

      // Check for specific file validation errors
      if (this.applicationForm.get('documentDatas')) {
        const documentDatasArray = this.documentDatas();
        documentDatasArray.controls.forEach((control: AbstractControl) => {
          if (control instanceof FormGroup) {
            const fileControl = control.get('file');
            if (fileControl?.errors) {
              if (fileControl.errors['maxSize']) {
                hasFileSizeError = true;
              }
              if (fileControl.errors['fileType']) {
                hasFileTypeError = true;
              }
            }
          }
        });
      }

      // Collect all invalid fields for logging
      Object.keys(this.applicationForm.controls).forEach(key => {
        const control = this.applicationForm.get(key);
        if (control?.invalid) {
          invalidFields.push(key);
          console.log(`Field ${key} validation errors:`, control.errors);
        }
      });
      console.log('Invalid fields:', invalidFields);

      // Show specific error message based on validation error type
      if (hasFileSizeError) {
        await swal.fire({
          title: 'File size too large',
          text: 'One or more files exceed the maximum allowed size.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else if (hasFileTypeError) {
        await swal.fire({
          title: 'Invalid file type',
          text: 'One or more files have an invalid file extension. Please upload only allowed file types.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else {
        await swal.fire({
          title: 'Please fill in all required fields before submitting.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
      return;
    }

    const existingFilesValid = this.reportData?.reportFiles && this.reportData.reportFiles.length > 0;

    const files = this.documentDatas().controls.length


    if (!existingFilesValid && files == 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'File Attachment Required',
        text: 'Please attach at least one file before submitting the application.',
      });
      return;
    }

    const submitData = this.prepareSubmitData();

    await this.spinner.show();
    serviceMethod(submitData).subscribe({
      next: async (result: any) => {
        // Without this guard a result:false body fell through to the upload and then showed
        // the success dialog on top of the interceptor error dialog.
        if (result?.result === false) {
          await this.spinner.hide();
          return;
        }

        const formData = this.createReportUploadFormData();
        try {
          await this.handleReportUpload(formData);
          await this.spinner.hide();
          swal.fire({
            title: successMessage,
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => this.navigateToDetails());
        } catch (error) {
          if (error instanceof Error) {
            console.log(error.message);
          } else {
            console.log('An unexpected error occurred', error);
          }
        }

      },
      error: () => {
        // Failure text and the support reference id come from HttpErrorInterceptor.
        this.spinner.hide();
      },
    });
  }

  private async loadReport(): Promise<void> {
    await this.spinner.show();
    return new Promise<void>((resolve, reject) => {
      this.grantApplicationReportService.getReportInfo(this.id).subscribe({
        next: (res) => {
          if (res?.result) {
            this.reportData = res.item ?? null;
            this.patchFormWithReportData();
            this.patchTargetGroupTableData();
            this.setupDynamicFinancialTasks();
            this.fetchProjectDataIfExists();
          }
          this.spinner.hide();
          resolve();
        },
        error: async (error) => {
          await this.spinner.hide();
          console.error('Error fetching projects:', error);



          reject(error);
        },
      });
    });
  }

  private patchFormWithReportData(): void {
    if (this.reportData) {
      const {
        amountReceive, amountSpent, budgetNextPeriod,
        amountRemaining, isCofunded, cofundingDescription,
        projectImplementDescription, otherPartnerrAndNetwork,
        crossCuttingIssue, whatStory,
      } = this.reportData;
      this.applicationForm.patchValue({
        amountReceive: amountReceive?.toString() ?? '',
        amountSpent: amountSpent?.toString() ?? '',
        budgetNextPeriod: budgetNextPeriod?.toString() ?? '',
        amountRemaining: amountRemaining?.toString() ?? '',
        isCofunded: isCofunded ? '1' : '0',
        cofundingDescription, projectImplementDescription,
        otherPartnerrAndNetwork, crossCuttingIssue, whatStory
      });
    }
  }

  private setupDynamicFinancialTasks(): void {
    if (this.reportData?.financialTask) {
      this.reportData.financialTask.forEach(task => {
        this.financialTasksFormArray.push(this.fb.group({
          id: task.seq,
          name: task.name,
          status: new FormControl(task.status || 'Not Complete'),
        }));

        this.documentType.push({key: task.refType ?? "", value: task.name})

      });


    }
  }

  private fetchProjectDataIfExists(): void {
    if (this.reportData?.projectId) {
      this.projectService.getProject(this.reportData.projectId).subscribe(async res => {
        if (res?.result) {
          this.projectData = res.item ?? null;
        } else {
          const errorMessage = res?.messages;
          await this.spinner.hide();
          swal.fire({
            title: "Error",
            text: errorMessage?.join(', ') || "An unexpected error occurred.",
            icon: "error",
          }).then(() => {

            this.router.navigate(['/applications']);
          });
          return;
        }
      });
    }
  }

  private prepareSubmitData(): any {
    const submitData: any = {
      id: this.reportData!.id!,
      financialReportStatuses: [],
      reportTypeCode: this.reportData?.reportTypeCode
    };

    this.addFinancialTaskStatuses(submitData);
    this.addReportDetails(submitData);

    this.AddTargetGroup(submitData);

    return submitData;
  }

  private addFinancialTaskStatuses(submitData: any): void {
    if (this.reportData?.financialTask) {
      this.reportData.financialTask.forEach((task, index) => {
        const status = this.financialTasksFormArray.at(index).get('status')?.value;
        submitData.financialReportStatuses.push({
          reportType: task.refType,
          isComplete: status === "Complete",
        });
      });
    }
  }

  private addReportDetails(submitData: any): void {
    const {reportTypeCode} = this.reportData!;

    if (reportTypeCode === "StatusReportNarrative" || reportTypeCode === "FinalReportNarrative") {
      const parseIntOrNull = (val: any) => (val !== null && val !== undefined && val !== '') ? parseInt(val, 10) : null;
      submitData.amountReceive = parseIntOrNull(this.applicationForm.get('amountReceive')?.value);
      submitData.amountSpent = parseIntOrNull(this.applicationForm.get('amountSpent')?.value);
      submitData.budgetNextPeriod = parseIntOrNull(this.applicationForm.get('budgetNextPeriod')?.value);
    }
    if (reportTypeCode === "FinalReportNarrative") {
      submitData.isCofunded = (this.applicationForm.get('isCofunded')?.value === '1');
      submitData.cofundingDescription = this.applicationForm.get('cofundingDescription')?.value ?? '';
      submitData.projectImplementDescription = this.applicationForm.get('projectImplementDescription')?.value ?? '';
      submitData.otherPartnerrAndNetwork = this.applicationForm.get('otherPartnerrAndNetwork')?.value ?? '';
      submitData.crossCuttingIssue = this.applicationForm.get('crossCuttingIssue')?.value ?? '';
      submitData.whatStory = this.applicationForm.get('whatStory')?.value ?? '';
    }
  }

  private createReportUploadFormData(): FormData {
    const formData = new FormData();

    formData.append('ReportId', this.id ?? '');


    this.documentDatas().controls.forEach((control: AbstractControl, index: number) => {
      if (control instanceof FormGroup) {
        const file = control.get('file')?.value;
        const docType = control.get('docType')?.value || '';

        if (file) {
          formData.append(`DocumentInfos[${index}].File`, file); // Append file
          formData.append(`DocumentInfos[${index}].ReportType`, docType); // Append ReportType
        }
      }
    });

    return formData;
  }

  private handleReportUpload(formData: FormData): Promise<any> {
    return new Promise<any>((resolve, reject) => {

      this.grantApplicationReportService.uploadDocument(formData).subscribe({
        next: (response: any) => {
          if (response?.result === false) {
            const errorMessage = response.messages?.join(' ') || 'An unexpected error occurred.';
            reject(new Error(errorMessage));
          } else {
            resolve(response);
          }
        },
        error: reject,
      });
    });
  }

  private patchTargetGroupTableData() {
    if (this.reportData) {
      this.reportData.targetGroupImpact.forEach((item) => {
        const maleCount = item.numberOfMale || 0;
        const femaleCount = item.numberOfFemale || 0;
        const otherCount = item.numberOfOther || 0;
        const total = item.impactedIndividualsTotal || 0;

        const group = this.fb.group({
          name: [item.name || ""],
          numberOfMale: [maleCount, this.amountFormatValidators],
          numberOfFemale: [femaleCount, this.amountFormatValidators],
          numberOfOther: [otherCount, this.amountFormatValidators],
          impactedIndividualsTotal: [total, this.amountFormatValidators],
          id: [item.id || ""]
        });



        this.targetGroupImpact.push(group);
      });
    }
  }



  private AddTargetGroup(submitData: any) {
    const targetGroupImpact = this.targetGroupImpact

    const parseIntOrZero = (val: any) => parseInt(val, 10) || 0;
    submitData.TargetGroups = targetGroupImpact.controls.map((control: AbstractControl) => {
      const formGroup = control as FormGroup;
      return {
        name: formGroup.get('name')?.value || '',
        numberOfMale: parseIntOrZero(formGroup.get('numberOfMale')?.value),
        numberOfFemale: parseIntOrZero(formGroup.get('numberOfFemale')?.value),
        numberOfOther: parseIntOrZero(formGroup.get('numberOfOther')?.value),
        impactedIndividualsTotal: parseIntOrZero(formGroup.get('impactedIndividualsTotal')?.value),
        id: formGroup.get('id')?.value || "",
      };
    });
  }





  private updateValidatorsBasedOnReportType(): void {
    const isNarrative = this.reportData?.reportTypeCode === 'FinalReportNarrative'
      || this.reportData?.reportTypeCode === 'StatusReportNarrative';

    // Only "required" is conditional. The range and whole-number rules always apply, because
    // Dataverse rejects out-of-range values whatever the report type.
    const amountValidators = isNarrative
      ? [Validators.required, ...this.amountFormatValidators]
      : this.amountFormatValidators;

    this.applicationForm.get('amountReceive')?.setValidators(amountValidators);
    this.applicationForm.get('amountSpent')?.setValidators(amountValidators);
    this.applicationForm.get('budgetNextPeriod')?.setValidators(amountValidators);
    // amountRemaining is intentionally left out: it is a read-only CRM formula value, never
    // submitted, so no report type makes it required.

    this.applicationForm.get('amountReceive')?.updateValueAndValidity();
    this.applicationForm.get('amountSpent')?.updateValueAndValidity();
    this.applicationForm.get('budgetNextPeriod')?.updateValueAndValidity();
  }

  private async handleError(error: SharedError): Promise<void> {
    if (error.redirectUrl) {

      console.log(`Redirecting to: ${error.redirectUrl}`);
      await this.router.navigate([error.redirectUrl]);
    } else {
      await this.router.navigate(['/'], {relativeTo: this.route});
    }

    console.log('Error details:', error.details);
  }


}
